import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import type { ReactElement } from 'react';

Font.register({
  family: 'Inter',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuGKYMZg.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZg.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Fira Mono',
  src: 'https://fonts.gstatic.com/s/firamono/v16/N0bX2SlFPv1weGeLZDtQIQ.ttf',
  fontWeight: 400,
});

export interface GuidePdfProps {
  examplePath: string;
  postExamplePath: string;
  uploadExamplePath: string;
  endpoints: Array<{ method: string; path: string; summary?: string; description?: string }>;
}

const METHOD_COLORS: Record<string, string> = {
  GET: '#DFF2E5', // $.surfacePositiveMedium
  POST: '#0060FF1F', // $.accentSky10  (rgba(0,96,255,0.12))
  PUT: '#FF950133', // $.accentPumpkin10 (rgba(255,149,1,0.2))
  PATCH: '#FFF7E0', // $.accentSun10
  DELETE: '#FFEAEA', // $.surfaceCriticalSilent
};

const METHOD_TEXT_COLORS: Record<string, string> = {
  GET: '#288344', // $.textPositive
  POST: '#375FC4', // $.colorSky80
  PUT: '#B55700', // $.colorPumpkin80
  PATCH: '#725C00', // $.colorSun80
  DELETE: '#CD3535', // $.textCritical
};

const s = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    fontSize: 10,
    paddingTop: 48,
    paddingBottom: 60,
    paddingHorizontal: 52,
    color: '#09090a',
    lineHeight: 1.6,
  },
  docTitle: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 32,
    color: '#09090a',
  },
  h2: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    marginTop: 40,
    color: '#09090a',
  },
  h3: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    marginTop: 20,
    color: '#09090a',
  },
  h4: {
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 4,
    marginTop: 16,
    color: '#09090a',
  },
  p: {
    fontSize: 10,
    color: '#52525b',
    marginBottom: 8,
    lineHeight: 1.7,
  },
  pPrimary: {
    fontSize: 10,
    color: '#09090a',
    marginBottom: 8,
    lineHeight: 1.7,
  },
  bold: {
    fontWeight: 700,
    color: '#09090a',
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 4,
    paddingLeft: 8,
  },
  bullet: {
    width: 14,
    fontSize: 10,
    color: '#09090a',
    fontWeight: 600,
  },
  listItemText: {
    flex: 1,
    fontSize: 10,
    color: '#52525b',
    lineHeight: 1.7,
  },
  listItemLabel: {
    fontWeight: 600,
    color: '#09090a',
  },
  codeBlock: {
    backgroundColor: '#F8F8FB',
    borderRadius: 10,
    marginBottom: 8,
    marginTop: 4,
    padding: 12,
  },
  codeText: {
    color: '#09090a',
    fontFamily: 'Fira Mono',
    fontSize: 8,
    fontWeight: 400,
    lineHeight: 1.6,
  },
  callout: {
    borderLeftWidth: 2,
    borderLeftColor: '#09090a',
    paddingLeft: 12,
    marginTop: 8,
    marginBottom: 8,
  },
  calloutLabel: {
    fontWeight: 600,
    fontSize: 10,
    color: '#09090a',
    marginBottom: 4,
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.06)',
    paddingTop: 0,
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
  // Table row — matches td: border10 bottom, paddingBlock 12
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  tableHeaderCell: {
    fontWeight: 600,
    fontSize: 9,
    color: '#09090a',
  },
  tableCell: {
    fontSize: 9,
    color: '#52525b',
    lineHeight: 1.5,
    overflow: 'hidden',
  },
  methodBadge: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 4,
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  methodBadgeText: {
    fontSize: 7,
    fontWeight: 600,
    lineHeight: 1,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 8,
    bottom: 24,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#a1a1aa',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e4e4e7',
    marginTop: 4,
    marginBottom: 4,
  },
});

export function GuidePdfDocument ({
  examplePath,
  postExamplePath,
  uploadExamplePath,
  endpoints,
}: GuidePdfProps): ReactElement {
  return (
    <Document author="TrendMD" title="Customer Integration API">
      <Page size="A4" style={s.page}>

        <Text style={s.docTitle}>Customer Integration API</Text>

        <Text style={s.h2}>Overview</Text>
        <Text style={s.p}>
          {'The Customer Integration API is the programmatic surface for managing campaigns, teams, files, and other customer resources from your own systems. Every request is signed with a secret key issued to your integration, so the server can verify both who you are and that the request has not been tampered with in transit.'}
        </Text>
        <Text style={s.p}>{'This page covers everything you need to call the API:'}</Text>
        <View style={s.listItem}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Credentials and the OpenAPI schema</Text>{' — where to get them.'}</Text>
        </View>
        <View style={s.listItem}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Authentication</Text>{' — how to sign requests so the server accepts them.'}</Text>
        </View>
        <View style={s.listItem}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Errors</Text>{' — what to do when a request is declined.'}</Text>
        </View>

        <Text style={s.h2}>Getting started</Text>

        <Text style={s.h3}>1. Generate an API key</Text>
        <Text style={s.p}>
          {'Open the customer dashboard and navigate to Settings \u2192 API Keys and click the + Create API key button. You will receive two values:'}
        </Text>
        <View style={s.listItem}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Api Key</Text>{' — a public identifier with the prefix tmdc_ak_. Goes in the X-Api-Key header on every request.'}</Text>
        </View>
        <View style={s.listItem}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Secret key</Text>{' — with the prefix tmdc_sk_. Shown only once. Store it in a secret manager (Vault, AWS Secrets Manager, 1Password, etc.) — you cannot recover it later.'}</Text>
        </View>
        <Text style={s.p}>{'Both values are tied to a specific organization and user. Revoking the key in the dashboard invalidates it immediately.'}</Text>

        <Text style={s.h3}>2. Download the OpenAPI schema</Text>
        <Text style={s.p}>
          {'The full endpoint catalog is published as an OpenAPI 3.x JSON document. Open it in any OpenAPI viewer (Swagger UI, Redoc, Stoplight) or load it into your code-generation tool of choice to scaffold a typed client.'}
        </Text>

        <View wrap={false}>
          <Text style={s.h2}>Authentication</Text>
          <Text style={s.p}>
            {'Every request must include three headers. Together they identify your integration, prove the request has not been altered in transit, and bound the request to a small time window so a captured request cannot be replayed later.'}
          </Text>
        </View>

        <Text style={s.h3}>Required headers</Text>
        <Text style={s.p}>{'Every request to the Customer Integration API must include all three of these headers.'}</Text>

        <View style={s.table}>
          <View style={s.tableHeaderRow}>
            <Text style={[ s.tableHeaderCell, { flex: 1 }]}>Header</Text>
            <Text style={[ s.tableHeaderCell, { flex: 1.2 }]}>Format</Text>
            <Text style={[ s.tableHeaderCell, { flex: 2 }]}>Example</Text>
            <Text style={[ s.tableHeaderCell, { flex: 2 }]}>Purpose</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 1 }]}>X-Api-Key</Text>
            <Text style={[ s.tableCell, { flex: 1.2 }]}>string, prefix tmdc_ak_</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>tmdc_ak_4f3e2d1c0b9a87654321</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>Identifies the integration making the request.</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 1 }]}>X-Timestamp</Text>
            <Text style={[ s.tableCell, { flex: 1.2 }]}>UNIX timestamp, seconds (int64)</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>1713321600</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>{'When the request was generated. Must be within \u00b15 minutes of the server\'s clock.'}</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 1 }]}>X-Signature</Text>
            <Text style={[ s.tableCell, { flex: 1.2 }]}>string, prefix sha256=, then hex</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>sha256=9a3f7c1e...</Text>
            <Text style={[ s.tableCell, { flex: 2 }]}>HMAC-SHA256 of the canonical string, signed with your secret key.</Text>
          </View>
        </View>

        <Text style={s.h3}>Computing the signature</Text>
        <Text style={s.p}>{'Signing is a three-step process:'}</Text>
        <View style={s.listItem}>
          <Text style={s.bullet}>1.</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Build the canonical string </Text>{'from the request — a small text representation derived from method, path, timestamp, and body.'}</Text>
        </View>
        <View style={s.listItem}>
          <Text style={s.bullet}>2.</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>HMAC-SHA256 </Text>{'that string with your secret key.'}</Text>
        </View>
        <View style={s.listItem}>
          <Text style={s.bullet}>3.</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Hex-encode the digest and prepend sha256=</Text>{'. Send the result as X-Signature.'}</Text>
        </View>
        <Text style={s.p}>{'The canonical string layout depends on the request type. There are three variants.'}</Text>

        {/* Variant A — keep heading + both code blocks together */}
        <View wrap={false}>
          <Text style={s.h4}>Variant A — GET (no body)</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`METHOD\nPATH\nTIMESTAMP`}</Text>
          </View>
          <Text style={s.p}>{'Three lines. No body component.'}</Text>
          <Text style={s.pPrimary}>{'Example:'}</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`GET\n${examplePath}\n1713321600`}</Text>
          </View>
        </View>

        {/* Variant B — keep heading + both code blocks together */}
        <View wrap={false}>
          <Text style={s.h4}>Variant B — Non-GET request with a JSON or form-urlencoded body</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`METHOD\nPATH\nTIMESTAMP\nRAW_BODY`}</Text>
          </View>
          <Text style={s.p}>
            {'Four lines. The fourth line is the request body byte-for-byte exactly as transmitted. Sign the final serialized body — do not re-serialize, re-format, or re-order keys after computing the signature, or it will not match.'}
          </Text>
          <Text style={s.pPrimary}>{'Example:'}</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`POST\n${postExamplePath}\n1713321600\n{"name":"Acme Inc","description":"Pilot account"}`}</Text>
          </View>
        </View>

        {/* Variant C — keep heading + both code blocks together */}
        <View wrap={false}>
          <Text style={s.h4}>Variant C — Multipart request with a file upload</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`METHOD\nPATH\nTIMESTAMP\nSHA256_HEX(file_bytes)`}</Text>
          </View>
          <Text style={s.p}>
            {'Four lines. The fourth line is the lowercase hex SHA-256 digest of the file\'s raw bytes — not the multipart envelope, not a base64 encoding, not the form-field metadata.'}
          </Text>
          <Text style={s.pPrimary}>{'Example:'}</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{`POST\n${uploadExamplePath}\n1713321600\nb94d27b9934d3e08a52e52d7da7dabfac484efe37a5380ee9088f7ace2efcde9`}</Text>
          </View>
        </View>

        {/* Callout — keep all notes together */}
        <View style={s.callout} wrap={false}>
          <Text style={s.calloutLabel}>Notes that apply to all three variants:</Text>
          <View style={s.listItem}>
            <Text style={s.bullet}>{'\u2022'}</Text>
            <Text style={s.listItemText}><Text style={s.listItemLabel}>PATH</Text>{' is the full request path including query string. GET /teams?limit=20 and GET /teams produce different canonical strings — the path you sign must match the path you send.'}</Text>
          </View>
          <View style={s.listItem}>
            <Text style={s.bullet}>{'\u2022'}</Text>
            <Text style={s.listItemText}><Text style={s.listItemLabel}>METHOD</Text>{' is uppercase: GET, POST, PUT, PATCH, DELETE.'}</Text>
          </View>
          <View style={s.listItem}>
            <Text style={s.bullet}>{'\u2022'}</Text>
            <Text style={s.listItemText}><Text style={s.listItemLabel}>Line separator</Text>{' is a single newline (\\n, 0x0A). No carriage returns. No trailing newline.'}</Text>
          </View>
        </View>

        {/* Node.js code sample — large block, keep heading + code together */}
        <View wrap={false}>
          <Text style={s.h3}>Code sample (Node.js)</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{[
              "const crypto = require('node:crypto');",
              '',
              'function sign({ method, path, timestamp, body, secretKey }) {',
              '  let canonical;',
              "  if (method === 'GET') {",
              // eslint-disable-next-line no-template-curly-in-string
              '    canonical = `${method}\\n${path}\\n${timestamp}`;',
              '  } else {',
              // eslint-disable-next-line no-template-curly-in-string
              '    canonical = `${method}\\n${path}\\n${timestamp}\\n${body}`;',
              '  }',
              "  const digest = crypto.createHmac('sha256', secretKey).update(canonical).digest('hex');",
              // eslint-disable-next-line no-template-curly-in-string
              '  return `sha256=${digest}`;',
              '}',
              '',
              'const timestamp = Math.floor(Date.now() / 1000);',
              '',
              'const headers = {',
              "  'X-Api-Key': 'tmdc_ak_...',",
              "  'X-Timestamp': timestamp.toString(),",
              "  'X-Signature': sign({",
              "    method: 'POST',",
              `    path: '${postExamplePath}',`,
              '    timestamp,',
              "    body: JSON.stringify({ name: 'Acme Inc' }),",
              "    secretKey: 'tmdc_sk_...',",
              '  }),',
              '};',
            ].join('\n')}</Text>
          </View>
        </View>

        {/* Multipart variant — keep label + code together */}
        <View wrap={false}>
          <Text style={s.p}>{'Multipart upload variant — replace body with the hex SHA-256 digest of the file\'s raw bytes:'}</Text>
          <View style={s.codeBlock}>
            <Text style={s.codeText}>{[
              "const fileHash = crypto.createHash('sha256').update(fileBuffer).digest('hex');",
              '',
              'const signature = sign({',
              "  method: 'POST',",
              `  path: '${uploadExamplePath}',`,
              '  timestamp,',
              '  body: fileHash,',
              "  secretKey: 'tmdc_sk_...',",
              '});',
            ].join('\n')}</Text>
          </View>
        </View>

        <Text break style={s.h2}>Available endpoints</Text>
        <Text style={s.p}>{'All endpoints share the same authentication scheme described above.'}</Text>

        {endpoints.length > 0 && (
          <View style={s.table}>
            <View style={s.tableHeaderRow}>
              <Text style={[ s.tableHeaderCell, { flex: 0.7 }]}>Method</Text>
              <Text style={[ s.tableHeaderCell, { flex: 2 }]}>Path</Text>
              <Text style={[ s.tableHeaderCell, { flex: 2 }]}>Description</Text>
            </View>
            {endpoints.map(({ method, path, summary, description }) => (
              <View key={`${method}-${path}`} style={s.tableRow} wrap={false}>
                <View style={{ flex: 0.7, alignItems: 'flex-start', justifyContent: 'center' }}>
                  <View style={[ s.methodBadge, { backgroundColor: METHOD_COLORS[method] ?? '#f4f4f5' }]}>
                    <Text style={[ s.methodBadgeText, { color: METHOD_TEXT_COLORS[method] ?? '#09090a' }]}>{method}</Text>
                  </View>
                </View>
                <Text style={[ s.tableCell, { flex: 2 }]}>{path}</Text>
                <Text style={[ s.tableCell, { flex: 2 }]}>{summary ?? description ?? ''}</Text>
              </View>
            ))}
          </View>
        )}

        <Text style={s.h2}>Errors</Text>

        <Text style={s.h3}>Quick reference</Text>
        <View style={s.table} wrap={false}>
          <View style={s.tableHeaderRow}>
            <Text style={[ s.tableHeaderCell, { flex: 0.6 }]}>Status</Text>
            <Text style={[ s.tableHeaderCell, { flex: 1.5 }]}>Meaning</Text>
            <Text style={[ s.tableHeaderCell, { flex: 2.5 }]}>First thing to check</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>400</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>Missing or malformed auth header</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>
              {'All three headers (X-Api-Key, X-Timestamp, X-Signature) present? Timestamp is an integer in seconds, not milliseconds?'}
            </Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>400</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>{'Timestamp outside \u00b15 minute window'}</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>Client clock drift. Sync to NTP.</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>400</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>Invalid signature</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>
              {'Canonical string mismatch — most often path missing the query string, body re-serialized after signing, or wrong newline.'}
            </Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>403</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>API key unknown / revoked / inactive</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>Was the key revoked, expired, or never activated?</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>403</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>Organization or user not active</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>Account status issue — contact your account manager.</Text>
          </View>
          <View style={s.tableRow} wrap={false}>
            <Text style={[ s.tableCell, { flex: 0.6 }]}>403</Text>
            <Text style={[ s.tableCell, { flex: 1.5 }]}>Secret key cannot verify the signature</Text>
            <Text style={[ s.tableCell, { flex: 2.5 }]}>
              {'Secret key out of sync with the API key (likely rotated on the server side, or you are signing with a key from a different integration).'}
            </Text>
          </View>
        </View>

        <Text style={s.h3}>Common pitfalls</Text>
        <View style={s.listItem} wrap={false}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Body must be byte-identical.</Text>{' For non-GET, non-multipart requests, the body in the canonical string is the exact bytes transmitted. If your HTTP client re-serializes JSON (re-orders keys, normalizes whitespace, changes number formatting) after you have signed, the signature will not match. Sign the final serialized body.'}</Text>
        </View>
        <View style={s.listItem} wrap={false}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Path must include the query string.</Text>{' GET /teams?limit=20 and GET /teams produce different canonical strings. The path you sign must match what your HTTP client actually sends.'}</Text>
        </View>
        <View style={s.listItem} wrap={false}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Timestamp is seconds, not milliseconds.</Text>{' Math.floor(Date.now() / 1000) — never Date.now().'}</Text>
        </View>
        <View style={s.listItem} wrap={false}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Hash the file bytes, not the multipart envelope.</Text>{' For uploads, compute SHA-256 over the raw file content only. Do not include multipart boundaries, part headers, or surrounding form fields.'}</Text>
        </View>
        <View style={s.listItem} wrap={false}>
          <Text style={s.bullet}>{'\u2022'}</Text>
          <Text style={s.listItemText}><Text style={s.listItemLabel}>Do not hex-encode the file before hashing.</Text>{' The fourth line is SHA256(file_bytes) \u2192 hex. Hash the raw bytes, then hex-encode the digest. Hex-encoding the file first and then hashing produces a different digest.'}</Text>
        </View>

        <Text
          fixed
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          style={s.pageNumber}
        />
      </Page>
    </Document>
  );
}
